import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { BookOpen, ArrowLeft, Download, ExternalLink } from 'lucide-react';
import api from '../../services/api';
import { useAuthStore } from '../../stores/authStore';

/**
 * DocsViewer - Display documentation markdown files
 * Context-aware: Shows different docs based on view mode
 */
const DocsViewer = () => {
  const { docId } = useParams();
  const navigate = useNavigate();
  const { viewMode } = useAuthStore();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const allDocs = {
    'admin-guide': {
      title: 'Administrator Guide',
      description: 'Complete guide for system administrators',
      file: 'ADMIN_GUIDE.md',
      allowedViews: ['platform_admin', 'company_admin']
    },
    'user-help': {
      title: 'User Help Guide',
      description: 'Friendly help for end users',
      file: 'USER_HELP_GUIDE.md',
      allowedViews: ['platform_admin', 'company_admin', 'end_user']
    },
    'technical': {
      title: 'Technical Documentation',
      description: 'Technical reference for developers',
      file: 'TECHNICAL_DOCUMENTATION.md',
      allowedViews: ['platform_admin']
    }
  };

  // Filter docs based on view mode
  const getAvailableDocs = () => {
    const filtered = {};
    Object.entries(allDocs).forEach(([key, doc]) => {
      if (doc.allowedViews.includes(viewMode)) {
        filtered[key] = doc;
      }
    });
    return filtered;
  };

  const availableDocs = getAvailableDocs();
  const currentDoc = allDocs[docId];

  // Check if user has access to this doc
  useEffect(() => {
    if (currentDoc && !currentDoc.allowedViews.includes(viewMode)) {
      // Redirect to appropriate page if user doesn't have access
      if (viewMode === 'end_user') {
        navigate('/my-verification');
      } else {
        navigate('/dashboard');
      }
    } else if (currentDoc) {
      fetchDocumentation();
    }
  }, [docId, viewMode]);

  const fetchDocumentation = async () => {
    setLoading(true);
    setError(null);
    try {
      // In production, this would fetch from API
      // For now, we'll show a placeholder
      const response = await api.get(`/docs/${currentDoc.file}`).catch(() => {
        // Fallback content if API endpoint doesn't exist yet
        return {
          data: `# ${currentDoc.title}\n\n${currentDoc.description}\n\n## Documentation\n\nThis documentation is available in the \`api/docs/${currentDoc.file}\` file.\n\n### Quick Links\n\n- [Admin Guide](/docs/admin-guide)\n- [User Help](/docs/user-help)\n- [Technical Docs](/docs/technical)\n\n### Features Covered\n\n- Identity Verification Sources\n- API Integration\n- Data Encryption\n- Multi-Document Face Matching\n- Video Selfie Verification\n- Platform Administration\n\n### Need Help?\n\nContact support@acquiva.com for assistance.`
        };
      });
      
      setContent(response.data);
    } catch (err) {
      setError('Failed to load documentation');
      console.error('Error loading docs:', err);
    } finally {
      setLoading(false);
    }
  };

  const downloadDoc = () => {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = currentDoc.file;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/dashboard"
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Dashboard
        </Link>
        
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <BookOpen className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{currentDoc.title}</h1>
              <p className="text-gray-600 mt-1">{currentDoc.description}</p>
            </div>
          </div>
          
          <button
            onClick={downloadDoc}
            className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Download className="w-4 h-4" />
            <span>Download</span>
          </button>
        </div>
      </div>

      {/* Quick Navigation - Only show docs available for current view mode */}
      {Object.keys(availableDocs).length > 1 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <p className="text-sm font-semibold text-gray-700 mb-3">Documentation</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(availableDocs).map(([key, doc]) => (
              <Link
                key={key}
                to={`/docs/${key}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  docId === key
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {doc.title}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        {error ? (
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={fetchDocumentation}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="prose prose-blue max-w-none">
            <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-gray-700">
              {content}
            </pre>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start space-x-3">
          <ExternalLink className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-blue-900">Need More Help?</p>
            <p className="text-sm text-blue-800 mt-1">
              Contact our support team at{' '}
              <a href="mailto:support@acquiva.com" className="underline">
                support@acquiva.com
              </a>
              {' '}or use the help button in the bottom right corner.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocsViewer;

